import { useQuery, useQueryClient } from "@tanstack/react-query";
import Package from "lucide-react/dist/esm/icons/package";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { WizardShell, type WizardStep } from "@/components/wizard/wizard-shell";
import { api, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { ClusterResponse } from "@/pages/cluster/types";
import { ConsoleMoved } from "./console-moved";
import { EnvironmentSummary } from "./environment-summary";
import { postSetupComplete } from "./setup-api";
import { AddWorkerStep } from "./steps/add-worker-step";
import { ClusterBasicsStep } from "./steps/cluster-basics-step";
import { CreateAdminStep } from "./steps/create-admin-step";
import { ReviewStep } from "./steps/review-step";
import { TlsStep } from "./steps/tls-step";
import type { TlsMode } from "./tls-api";
import { SETUP_STATUS_KEY, useSetupStatus } from "./use-setup-status";

type StepId = "admin" | "cluster" | "tls" | "worker" | "review";

interface StepMeta {
	/** Outcome-based rail label. */
	label: string;
	title: string;
	description: string;
	/** "Why this step matters" blurb shown in the context rail. */
	why: string;
}

const STEP_META: Record<StepId, StepMeta> = {
	admin: {
		label: "Create admin",
		title: "Create your admin account",
		description: "Set up the first administrator for this cluster.",
		why: "The first administrator owns this cluster and can invite operators later.",
	},
	cluster: {
		label: "Name & domains",
		title: "Cluster basics",
		description: "Name your cluster and the hostnames it serves.",
		why: "Your hostname is where operators reach the console and automation connects over wss://.",
	},
	tls: {
		label: "HTTPS",
		title: "TLS",
		description: "Choose how HTTPS is terminated.",
		why: "This is how browsers trust the host. Automatic HTTPS issues a real certificate for you.",
	},
	worker: {
		label: "Add a worker",
		title: "Add a worker",
		description: "Provision a browser worker to handle sessions.",
		why: "Workers run the actual browser sessions behind the load balancer.",
	},
	review: {
		label: "Review & launch",
		title: "Review & launch",
		description: "Render the config, reload the LB, and go live.",
		why: "Render the config, reload OpenResty, and confirm the load balancer is healthy.",
	},
};

function normalizeTlsMode(mode: string | undefined): TlsMode | null {
	if (mode === "acme" || mode === "cloudflare" || mode === "byo") return mode;
	return null;
}

function tlsLabel(mode: string | null): string | null {
	switch (mode) {
		case "acme":
			return "Automatic HTTPS";
		case "cloudflare":
			return "Cloudflare / HTTP";
		case "byo":
			return "Custom certificate";
		default:
			return null;
	}
}

/**
 * First-run onboarding wizard. Gated by GET /api/system/setup-status (via the
 * App shell): shown standalone when no admin exists, or as a full-screen
 * takeover for an authed admin whose cluster/render isn't configured yet.
 *
 * Chrome (split-screen shell, vertical stepper, motion, focus management)
 * lives in WizardShell; each step file owns just its form + actions.
 */
export function SetupWizard() {
	const { user } = useAuth();
	const { data: status } = useSetupStatus();
	const queryClient = useQueryClient();
	const [, navigate] = useLocation();
	const [evaluation, setEvaluation] = useState(false);

	// Running environment summary for the rail. Refetched as steps invalidate
	// the cluster cache, so hostname/worker count fill in as they're saved.
	const clusterQuery = useQuery({
		queryKey: ["cluster"],
		queryFn: () => unwrap(api.api.cluster.get()) as Promise<ClusterResponse>,
		staleTime: 5_000,
	});

	// Stable 5-step list: the admin step never disappears — once the admin
	// exists it renders as completed (checked) but is locked (an admin can't
	// be re-created), so the rail keeps its numbering after step 1.
	const steps = useMemo<WizardStep[]>(
		() => [
			{
				id: "admin",
				label: STEP_META.admin.label,
				completed: !!user,
				locked: true,
			},
			{ id: "cluster", label: STEP_META.cluster.label },
			{ id: "tls", label: STEP_META.tls.label },
			{ id: "worker", label: STEP_META.worker.label },
			{ id: "review", label: STEP_META.review.label },
		],
		[user],
	);

	const [step, setStep] = useState<StepId>(() => {
		if (!user) return "admin";
		if (status?.needsCluster) return "cluster";
		return "review";
	});

	// If the admin was just created (user became set) but we're still on the
	// admin step, advance so the flow continues without a manual click.
	useEffect(() => {
		if (user && step === "admin") setStep("cluster");
	}, [user, step]);

	const currentIndex = Math.max(
		0,
		steps.findIndex((s) => s.id === step),
	);

	const goTo = (id: StepId) => setStep(id);
	const goNext = () => {
		const next = steps[currentIndex + 1];
		if (next) goTo(next.id as StepId);
	};
	const goBack = () => {
		const prev = steps[currentIndex - 1];
		if (prev) goTo(prev.id as StepId);
	};

	// Set when finish determined the console now lives on a different origin
	// (wizard driven over raw IP, hardened edge cuts it off) — swaps the shell
	// for the redirect interstitial.
	const [moved, setMoved] = useState<{
		consoleUrl: string;
		handoffPath: string | null;
	} | null>(null);

	const finish = async () => {
		try {
			// Ends the bootstrap grace: stamps setupCompletedAt and kicks the
			// hardened render+reload in the background.
			const res = await postSetupComplete();
			if (res.consoleUrl) {
				const target = new URL(res.consoleUrl);
				const here = window.location;
				if (
					target.hostname !== here.hostname ||
					target.protocol !== here.protocol
				) {
					setMoved({
						consoleUrl: res.consoleUrl,
						handoffPath: res.handoffPath,
					});
					return;
				}
			}
		} catch (err) {
			// Non-fatal: the cluster is already rendered + live from the review
			// step; completion hardening can be retried from settings.
			toast.error(
				err instanceof Error ? err.message : "Could not finalize setup",
			);
		}
		await queryClient.invalidateQueries({ queryKey: SETUP_STATUS_KEY });
		navigate("/");
	};

	const meta = STEP_META[step];
	const cluster = clusterQuery.data?.cluster ?? null;
	const hostname = cluster?.publicHostnames?.[0] ?? null;
	const workers = clusterQuery.data?.stats?.totalHosts ?? null;

	const initialTlsMode: TlsMode =
		normalizeTlsMode(status?.tlsMode) ??
		(hostname && !evaluation ? "acme" : "cloudflare");

	// Rail hint: where the console will live after launch, when that differs
	// from the origin the wizard is currently being driven from (raw IP).
	const consoleHost = cluster?.managerHostname?.trim() || hostname;
	const movesTo =
		consoleHost &&
		!evaluation &&
		(window.location.hostname !== consoleHost ||
			window.location.protocol !== "https:")
			? `https://${consoleHost}`
			: null;

	const summary = {
		hostname,
		tls: tlsLabel(normalizeTlsMode(status?.tlsMode)),
		workers,
		evaluation,
		movesTo,
	};

	if (moved) {
		return (
			<ConsoleMoved
				consoleUrl={moved.consoleUrl}
				handoffPath={moved.handoffPath}
			/>
		);
	}

	return (
		<WizardShell
			steps={steps}
			currentIndex={currentIndex}
			onStepSelect={(i) => {
				const target = steps[i];
				if (target) goTo(target.id as StepId);
			}}
			railTitle="Cluster Manager setup"
			railIcon={Package}
			title={meta.title}
			description={meta.description}
			why={meta.why}
			summary={<EnvironmentSummary summary={summary} />}
			version={status?.version}
			stepKey={step}
		>
			{step === "admin" && <CreateAdminStep onNext={goNext} />}
			{step === "cluster" && (
				<ClusterBasicsStep
					evaluation={evaluation}
					onEvaluationChange={setEvaluation}
					onNext={goNext}
					onBack={goBack}
				/>
			)}
			{step === "tls" && (
				<TlsStep
					initialMode={initialTlsMode}
					hostname={hostname}
					evaluation={evaluation}
					onNext={goNext}
					onBack={goBack}
				/>
			)}
			{step === "worker" && <AddWorkerStep onNext={goNext} onBack={goBack} />}
			{step === "review" && <ReviewStep onBack={goBack} onFinish={finish} />}
		</WizardShell>
	);
}
